import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { a as createLogger, d as isNumber, e as emitEvent, l as logDeprecatedEventOnce, b as clamp } from './utils.js';
import { d as defineCustomElement$3 } from './cl-icon2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-pagination';

const clPaginationCss = ".sc-cl-pagination-h{display:flex;height:var(--spacing-4)}.sc-cl-pagination-h button.sc-cl-pagination{outline:none}.sc-cl-pagination-h .pagination__results.sc-cl-pagination{font-family:var(--font-family-ui);line-height:var(--spacing-4);margin-right:var(--spacing-3)}.sc-cl-pagination-h .pagination__chevron.sc-cl-pagination{align-items:center;background-color:transparent;border:none;border-radius:var(--spacing-4);color:rgba(var(--color-neutral-900));cursor:pointer;display:flex;height:var(--spacing-4);justify-content:center;width:var(--spacing-4)}.sc-cl-pagination-h .pagination__chevron.sc-cl-pagination:hover{background-color:rgba(var(--color-brand-main), var(--opacity-1))}.sc-cl-pagination-h .pagination__chevron--disabled.sc-cl-pagination{color:rgba(var(--color-neutral-300));cursor:default;pointer-events:none}.sc-cl-pagination-h .pagination__chevron--disabled.sc-cl-pagination:hover{background-color:rgba(var(--color-neutral-100))}.sc-cl-pagination-h .pagination__page__dots.sc-cl-pagination{align-items:center;display:flex;justify-content:center}.sc-cl-pagination-h .pagination__page__number.sc-cl-pagination{align-items:center;background-color:transparent;border:none;border-radius:var(--spacing-4);color:rgb(var(--font-color-onlight-default));cursor:pointer;display:flex;font-weight:var(--font-weight-400);justify-content:center;line-height:var(--spacing-4);margin:0;min-height:var(--spacing-4);min-width:var(--spacing-4);white-space:nowrap}.sc-cl-pagination-h .pagination__page__number--active.sc-cl-pagination{color:rgba(var(--font-color-onlight-brand));position:relative}.sc-cl-pagination-h .pagination__page__number--active.sc-cl-pagination::after{border-bottom:2px solid rgba(var(--color-brand-600));border-radius:var(--border-radius-interactive-small);bottom:0;content:\"\";left:50%;position:absolute;transform:translateX(-50%);width:var(--spacing-1)}.sc-cl-pagination-h .pagination__page__number.sc-cl-pagination:hover{background-color:rgba(var(--color-brand-main), var(--opacity-1))}.sc-cl-pagination-h .pagination__go-to-page.sc-cl-pagination{align-items:center;background-color:rgba(var(--color-neutral-100));border-radius:var(--border-radius-interactive-small);color:rgb(var(--font-color-onlight-default));cursor:pointer;display:flex;font-family:var(--font-family-ui);font-size:var(--font-size-body-1);justify-content:center;margin-left:var(--spacing-3)}.sc-cl-pagination-h .pagination__go-to-page--button.sc-cl-pagination{background-color:transparent;border:none;inline-size:-moz-max-content;inline-size:max-content;padding:0 var(--spacing-2) 0 var(--spacing-2)}.sc-cl-pagination-h .pagination__go-to-page--input.sc-cl-pagination{color:rgb(var(--font-color-onlight-default));font-family:var(--font-family-ui);font-size:var(--font-size-body-1);padding:0 var(--spacing-1) 0 var(--spacing-2)}.sc-cl-pagination-h .pagination__go-to-page--input.sc-cl-pagination input.sc-cl-pagination{background-color:transparent;border:none;caret-color:rgba(var(--color-brand-main));outline:none;width:var(--spacing-7)}.sc-cl-pagination-h .pagination__go-to-page--input.sc-cl-pagination input.sc-cl-pagination::-webkit-outer-spin-button,.sc-cl-pagination-h .pagination__go-to-page--input.sc-cl-pagination input.sc-cl-pagination::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}.sc-cl-pagination-h .pagination__go-to-page--input.sc-cl-pagination input[type=number].sc-cl-pagination{-moz-appearance:textfield}.sc-cl-pagination-h .pagination__go-to-page--input__chevron.sc-cl-pagination{color:rgba(var(--color-neutral-900));margin-left:var(--spacing-1)}.sc-cl-pagination-h .pagination__go-to-page.sc-cl-pagination:hover{background-color:rgba(var(--color-brand-main), var(--opacity-1))}";

const ClPagination$1 = /*@__PURE__*/ proxyCustomElement(class ClPagination extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clChange = createEvent(this, "clChange", 7);
    this.change = createEvent(this, "pageChanged", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Creates a page button based on the pageNum
     */
    this.createPageButton = (pageNum, goToPageFn) => (h("button", { class: `pagination__page__number ${pageNum === this.currentPage && 'pagination__page__number--active'}`, onClick: goToPageFn }, pageNum));
    /**
     * Get all the pages to display inbetween the chevrons.
     */
    this.createAllPages = () => {
      const result = [];
      // If the amount of pages is lower than the amount of max pages, display them all.
      if (this.numPages <= this.max) {
        for (let i = 1; i <= this.numPages; i += 1) {
          result.push(this.createPageButton(i, () => this.gotoPage(i)));
        }
        return result;
      }
      result.push(this.createPageButton(1, () => this.gotoPage(1)));
      const renderStart = this.currentPage < this.max - 2;
      const renderEnd = this.numPages - this.currentPage <= 3;
      const renderMiddle = !renderStart && !renderEnd;
      let from;
      let to;
      // Start section
      if (renderStart) {
        from = 2;
        to = this.max - 2;
      }
      // Middle section. Only rendered if there is no start and end section.
      if (renderMiddle) {
        from = this.currentPage - Math.floor((this.max - 4) / 2);
        to = this.currentPage + Math.ceil((this.max - 4) / 2 - 1);
      }
      // End section
      if (renderEnd) {
        from = this.numPages - this.max + 3;
        to = this.numPages - 1;
      }
      if (renderMiddle || renderEnd) {
        result.push(h("div", { class: 'pagination__page__dots' }, "..."));
      }
      if (isNumber(from) && isNumber(to)) {
        for (let i = from; i <= to; i += 1) {
          result.push(this.createPageButton(i, () => this.gotoPage(i)));
        }
      }
      if (renderStart || renderMiddle) {
        result.push(h("div", { class: 'pagination__page__dots' }, h("cl-typography", null, "...")));
      }
      result.push(this.createPageButton(this.numPages, () => this.gotoPage(this.numPages)));
      return result;
    };
    /**
     * Updates the current page and emits the pageChanged event
     */
    this.gotoPage = (newPage) => {
      emitEvent(this, 'clChange', { component, current: newPage, previous: this.currentPage }, newPage);
      this.currentPage = newPage;
    };
    /**
     * Handles going to the next page
     */
    this.goToNextPage = () => {
      this.gotoPage(this.currentPage + 1);
    };
    /**
     * Handles going to the previous page
     */
    this.goToPreviousPage = () => {
      this.gotoPage(this.currentPage - 1);
    };
    /**
     * Get the jsx snippet for the goto page button.
     */
    this.getGotoPageButton = () => (h("button", { class: 'pagination__go-to-page pagination__go-to-page--button', onClick: this.toggleShowInput }, h("cl-typography", null, this.inputLabel)));
    /**
     * Get the jsx snippet for the goto page input,
     * shown after the goto page button was pressed.
     */
    this.getGotoPageInput = () => (h("div", { class: 'pagination__go-to-page pagination__go-to-page--input' }, h("input", { ref: el => {
        this.gotoPageInput = el;
      }, max: this.numPages, maxLength: 5, min: 1, type: 'number', onBlur: this.toggleShowInput, onChange: this.handleInputChange }), h("div", { class: 'pagination__go-to-page--input__chevron' }, h("cl-icon", { name: 'micro--chevron--right', size: 2 }))));
    /**
     * Handles going to the correct to the correct page based on the input value
     */
    this.handleInputChange = (event) => {
      const value = parseInt(event.target.value, 10);
      const page = clamp(value, 1, this.numPages);
      this.gotoPage(page);
      if (this.gotoPageInput) {
        // correct value when its out of allowed bounds
        this.gotoPageInput.value = `${page}`;
      }
    };
    /**
     * Set the showGotoPageInput state to true/false based on the current state
     */
    this.toggleShowInput = () => {
      this.showGotoPageInput = !this.showGotoPageInput;
    };
    this.showGotoPageInput = false;
    this.currentPage = 1;
    this.hideInput = false;
    this.inputLabel = '';
    this.max = 8;
    this.resultsPerPage = 15;
    this.text = '';
    this.totalResults = 0;
  }
  /**
   * Updates the numpages when the number of results OR the results per page change
   */
  handleResults() {
    this.numPages = Math.ceil(this.totalResults / this.resultsPerPage);
  }
  /**
   * Handles validating whether the inputLabel is set or not when hideInput is false
   */
  validateInputLabel() {
    if (!this.hideInput && !this.inputLabel.length) {
      this.logger.error.attribute.inputLabel.hideInput("'input-label' is required when 'hide-input' is set to false");
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Round up and spits greater or equal number of the given input
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'pageChanged', 'clChange');
    this.handleResults();
    this.validateInputLabel();
  }
  /**
   * Focus the input if it is shown and has a reference.
   */
  componentDidUpdate() {
    if (this.gotoPageInput && this.showGotoPageInput) {
      this.gotoPageInput.focus();
    }
  }
  render() {
    const { currentPage, hideInput, inputLabel } = this;
    return (h(Host, null, h("cl-typography", { class: 'pagination__results' }, this.text), this.numPages > 0 && [
      h("button", { class: `pagination__chevron ${currentPage === 1 && 'pagination__chevron--disabled'} pagination__chevron__left`, onClick: this.goToPreviousPage }, h("cl-icon", { name: 'micro--chevron--left', size: 2 })),
      this.createAllPages(),
      h("button", { class: `pagination__chevron  ${currentPage === this.numPages && 'pagination__chevron--disabled'} pagination__chevron__right`, onClick: this.goToNextPage }, h("cl-icon", { name: 'micro--chevron--right', size: 2 })),
      !hideInput && inputLabel && (this.showGotoPageInput ? this.getGotoPageInput() : this.getGotoPageButton()),
    ]));
  }
  static get watchers() { return {
    "resultsPerPage": ["handleResults"],
    "totalResults": ["handleResults"],
    "inputLabel": ["validateInputLabel"],
    "hideInput": ["validateInputLabel"]
  }; }
  static get style() { return clPaginationCss; }
}, [2, "cl-pagination", {
    "currentPage": [1538, "current-page"],
    "hideInput": [4, "hide-input"],
    "inputLabel": [1, "input-label"],
    "max": [514],
    "resultsPerPage": [2, "results-per-page"],
    "text": [1],
    "totalResults": [2, "total-results"],
    "showGotoPageInput": [32]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-pagination", "cl-icon", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-pagination":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClPagination$1);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClPagination = ClPagination$1;
const defineCustomElement = defineCustomElement$1;

export { ClPagination, defineCustomElement };

//# sourceMappingURL=cl-pagination.js.map